/**
 * @render react
 * @name Link
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';
import { AnchorBlock } from 'Components/atoms/AnchorBlock';

const ssr = typeof window === 'undefined';

/**
 * @type {module:url}
 * @returns {object}
 */
const url = require('url');

/**
 * Parse href
 * @param href
 * @returns {{hash: (string|null), hostname: (string|null), pathname: string, search: (string|null)}|{hash: string, hostname: string, pathname: string, search: string}}
 */
const parseUrl = (href) => {
  if (!ssr) {
    // The easy way to parse an URL, is to create <a> element.
    // @see: https://gist.github.com/jlong/2428561
    const parser = document.createElement('a');
    parser.href = href;
    return {
      hash: parser.hash !== '' ? parser.hash : null,
      hostname: parser.hostname,
      pathname: parser.pathname,
      search: parser.search !== '' ? parser.search : null,
    };
  }

  /**
   * Parse an address with the url.parse() method
   * url.parse* (urlString[, parseQueryString[, slashesDenoteHost]])
   */
  return {
    hash: url.parse(href).hash,
    hostname: url.parse(href).hostname,
    pathname: url.parse(href).pathname,
    search: url.parse(href).search,
  };
};

/**
 * Checks if the href provided is internal
 * @param {string} href
 * @param {string} siteUrl
 * @returns {boolean}
 */
export const isInternal = (href, siteUrl = null) => {
  const isSSR = ssr || typeof window === 'undefined';

  if (href.startsWith('/')
    || href.startsWith('#')
    || (!isSSR && parseUrl(href).hostname === window.location.hostname)) {
    return true;
  }

  let siteDomain = null;
  if (!isSSR) {
    siteDomain = window.location.hostname.replace('www.', '');
  }

  // siteUrl must be passed via polaris context in order to determine the link target on server side rendering.
  // See https://creativesolutions.atlassian.net/browse/PTWUK-236
  if (isSSR && siteUrl) {
    siteDomain = parseUrl(siteUrl).hostname.replace('www.', '');
  }

  // absolute URLs with same domain but different subdomains
  if (siteDomain && parseUrl(href).hostname && parseUrl(href).hostname.includes(siteDomain)) {
    return true;
  }

  // the URL is not internal
  return false;
};

/**
 * Link component
 *
 * @param {string} ariaLabel
 * @param {node} children - Components to be added as a child to the Link - can be another component, text or HTML elements
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {boolean} disableBrowserRouter - flag to determine if the React Router should be disabled. Default: false.
 * @param {string} download - The download attribute containing a filename. Optional
 * @param {object} extraClassNames - Extra classes to be added to the component
 * @param {string} href - Href attribute
 * @param {string} id - id attribute (gets assigned in the WYSIWYG)
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {string} name - name attribute (gets assigned in the WYSIWYG)
 * @param {Function} onClick - onClick callback
 * @param {string} rel - rel attribute (relationship) (gets assigned in the WYSIWYG)
 * @param {string} target - target attribute. Optional
 */
const Link = ({
  ariaLabel,
  children,
  context: {
    cssPrefix,
    LinkComponent,
    disableBrowserRouter,
    siteUrl,
  },
  dataAttributes = {},
  download,
  extraClassNames,
  href,
  id,
  loadStyles,
  name,
  onClick,
  role,
  rel,
  screenReaderText,
  tabIndex,
  target,
  title,
}) => {
  // Check if the link is CKEditor anchor block
  const isAnchorBlock = extraClassNames && extraClassNames['ck-anchor'];
  // links which have CKEditor anchor block class ('ck-anchor')
  if (isAnchorBlock) {
    return (
      <AnchorBlock id={id} extraClassNames={extraClassNames}>
        {children}
      </AnchorBlock>
    );
  }

  // for all links which are not links
  if (!href && (!id || !name)) {
    if (children) {
      return children;
    }

    return false;
  }

  // Check if the link internal
  // if href is not passed, treat link as internal (example: anchor links)
  const linkIsInternal = href ? isInternal(href, siteUrl) : true;

  const linkProps = {
    'aria-label': ariaLabel,
    className: classNames({
      [`${cssPrefix}__link`]: loadStyles,
      '-is-anchored-block': !href && (name || id),
      '-is-custom-link': LinkComponent,
      '-is-external': !linkIsInternal,
      ...extraClassNames,
    }),
    ...dataAttributes,
    download,
    id,
    name,
    onClick,
    rel: rel || ((target === '_blank' || !linkIsInternal) ? 'noopener' : undefined),
    role,
    tabIndex,
    target: target || (!linkIsInternal ? '_blank' : null),
    title,
  };

  // link with react router enabled
  if ((!disableBrowserRouter) && linkIsInternal) {
    if (parseUrl(href) && parseUrl(href).hash !== null) {
      return (
        <HashLink
          {...linkProps}
          to={parseUrl(href) || href}
          smooth
        >
          {children}
          {screenReaderText && (
            <span className="screen-reader-text">{screenReaderText}</span>
          )}
        </HashLink>
      );
    }

    return (
      <RouterLink
        {...linkProps}
        to={parseUrl(href) || href}
      >
        {children}
        {screenReaderText && (
          <span className="screen-reader-text">{screenReaderText}</span>
        )}
      </RouterLink>
    );
  }

  // for all links and anchor block
  return (
    <a
      {...linkProps}
      href={href}
    >
      {children}
      {screenReaderText && (
        <span className="screen-reader-text">{screenReaderText}</span>
      )}
    </a>
  );
};

/**
 * Valid prop types
 */
Link.propTypes = {
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  context: PropTypes.shape(),
  dataAttributes: PropTypes.shape(),
  download: PropTypes.string,
  extraClassNames: PropTypes.shape(),
  href: PropTypes.string,
  id: PropTypes.string,
  loadStyles: PropTypes.bool,
  name: PropTypes.string,
  onClick: PropTypes.func,
  rel: PropTypes.string,
  role: PropTypes.string,
  screenReaderText: PropTypes.string,
  tabIndex: PropTypes.oneOf([
    PropTypes.number,
    PropTypes.bool,
    PropTypes.string,
  ]),
  target: PropTypes.string,
  title: PropTypes.string,
};

/** Default props */
Link.defaultProps = {
  ariaLabel: null,
  children: [],
  context: {
    cssPrefix: '',
    LinkComponent: null,
    disableBrowserRouter: false,
    siteUrl: null,
  },
  dataAttributes: {},
  download: null,
  extraClassNames: {},
  href: null,
  id: null,
  loadStyles: true,
  name: null,
  onClick: () => {},
  rel: null,
  role: null,
  screenReaderText: null,
  tabIndex: null,
  target: null,
  title: null,
};

export default withPolaris(Link);
